import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsAdmin } from '../api/auth';
import { cropPlate, detectPlates } from '../lib/detectPlates';
import { readLabels, suggestClass, type ReadLabel } from '../lib/readLabels';

/**
 * The classes a classifier answers with.
 *
 * Built around the number that decides whether any of this works: how many
 * labelled images each class has. A list of eighty names tells you nothing — all
 * eighty look equally ready — while fine-grained classification fails precisely
 * on the ones nobody photographed. So the count is the second column, the
 * weakest classes float to the top of the readiness panel, and the page says
 * plainly whether training is possible yet and what is blocking it.
 *
 * Nobody types eighty cuts in one at a time either, so paste is the primary
 * path and the single-item form is the exception.
 */

interface Klass {
  id: number;
  code: string;
  name: string;
  family: string | null;
  classIndex: number | null;
  active: boolean;
  images: number;
  inspections: number;
  /** Position on a scale, or null when the classes are identities. */
  rank: number | null;
  scale: string | null;
  /** The primary reference image, if one has been uploaded. */
  referenceKey: string | null;
}

interface Readiness {
  minimumPerClass: number;
  classes: number;
  ready: number;
  thin: number;
  empty: number;
  total: number;
  weakest: { id: number; code: string; name: string; images: number }[];
  trainable: boolean;
  blockedBy: string | null;
}

interface Product {
  id: number;
  name: string;
  task?: string;
}

const cell: React.CSSProperties = { textAlign: 'left', padding: '7px 10px' };
const num: React.CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export function ClassesView() {
  const isAdmin = useIsAdmin();
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [classes, setClasses] = useState<Klass[]>([]);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [search, setSearch] = useState('');
  const [paste, setPaste] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/products')
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: Product[]) => {
        /**
         * Only products that HAVE classes.
         *
         * The page offered every product and opened on the first, which sorts
         * alphabetically and was an anomaly product — so it showed an empty
         * table with no columns and no upload control, and read as a feature
         * that had not been built. An anomaly product has no classes by
         * definition; offering it is offering a dead end.
         */
        const withClasses = rows.filter((p) => (p.task ?? 'anomaly') === 'classification');
        setProducts(withClasses);
        setProductId((cur) => cur ?? withClasses[0]?.id ?? null);
      })
      .catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    if (!productId) return;
    try {
      const [c, r] = await Promise.all([
        fetch(`/api/products/${productId}/classes`).then((x) => x.json()),
        fetch(`/api/products/${productId}/classes/readiness`).then((x) => x.json()),
      ]);
      setClasses(c);
      setReadiness(r);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const importList = async () => {
    setError(null);
    try {
      const res = await fetch(`/api/products/${productId}/classes/import`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text: paste }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message ?? 'Import failed');
      // Rejections are surfaced, never counted as success: a dropped line is a
      // cut that will never be classified, found out months later on the line.
      setNotice(
        `${body.created} added, ${body.updated} updated` +
          (body.rejected.length ? ` — ${body.rejected.length} line(s) not understood` : '') +
          (body.duplicates.length ? `, duplicates in your list: ${body.duplicates.join(', ')}` : ''),
      );
      if (body.rejected.length) {
        setError(
          `Not imported:\n${body.rejected.map((r: { line: string; why: string }) => `  ${r.line} — ${r.why}`).join('\n')}`,
        );
      }
      setPaste('');
      setShowPaste(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const patch = async (id: number, body: Record<string, unknown>) => {
    setError(null);
    const res = await fetch(`/api/product-classes/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) setError((await res.json()).message ?? 'Could not save');
    await load();
  };

  const remove = async (k: Klass) => {
    if (!confirm(`Delete class "${k.name}"? This cannot be undone.`)) return;
    setError(null);
    const res = await fetch(`/api/product-classes/${k.id}`, { method: 'DELETE' });
    if (res.status !== 204) setError((await res.json()).message ?? 'Could not remove');
    await load();
  };

  if (!isAdmin) return <div className="panel muted">Classes require the ADMIN role.</div>;

  if (products.length === 0) {
    return (
      <div>
        <h1 className="view-title">Classes</h1>
        <div className="panel muted">
          No classification product yet. Classes belong to a product that asks “which one is it?”
          — set one up in Settings, then its classes appear here.
        </div>
      </div>
    );
  }

  const shown = classes.filter(
    (k) =>
      !search ||
      `${k.code} ${k.name} ${k.family ?? ''}`.toLowerCase().includes(search.toLowerCase()),
  );
  const families = [...new Set(classes.map((k) => k.family).filter(Boolean))];

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Classes</h1>
        <div className="row" style={{ gap: 8 }}>
          <select
            value={productId ?? ''}
            onChange={(e) => setProductId(Number(e.target.value))}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button className="btn primary" onClick={() => setShowPaste((v) => !v)}>
            {showPaste ? 'Cancel' : '⊕ Paste a list'}
          </button>
        </div>
      </div>

      <p className="muted" style={{ marginTop: -6, maxWidth: 700 }}>
        What a classifier can answer with — the cuts, articles or product types this line sorts
        into. Not the same thing as defects: a defect is a fault, a class is an identity.
      </p>

      {showPaste && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
            One per line: <code>CODE, Name, Family</code> — comma, semicolon or tab. Lines starting
            with <code>#</code> are ignored. An existing code is updated, so re-pasting a corrected
            list is safe.
          </div>
          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={10}
            placeholder={'HAM-01, Ham bone-in, Ham\nHAM-02, Ham boneless, Ham\nNEK-01, Neck, Neck'}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
          />
          <button className="btn primary" style={{ marginTop: 8 }} onClick={() => void importList()}>
            Import
          </button>
        </div>
      )}

      <ChartDrop classes={shown} onChanged={load} />

      {readiness && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="row" style={{ gap: 26, flexWrap: 'wrap' }}>
            <Stat n={readiness.classes} label="classes" />
            <Stat n={readiness.total} label="labelled images" />
            <Stat n={readiness.ready} label={`at ${readiness.minimumPerClass}+`} tone="good" />
            <Stat n={readiness.thin} label="too thin" tone={readiness.thin ? 'warn' : undefined} />
            <Stat n={readiness.empty} label="with nothing" tone={readiness.empty ? 'bad' : undefined} />
          </div>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            {readiness.trainable ? (
              <span style={{ color: 'var(--good)' }}>Trainable.</span>
            ) : (
              <span style={{ color: 'var(--warn, #f59e0b)' }}>
                Not trainable yet — {readiness.blockedBy}
              </span>
            )}{' '}
            <span className="muted">
              {readiness.minimumPerClass} verified images per class is the target before a first
              production model. An average hides the problem: forty classes at 800 and forty at 3
              averages out healthy and produces a model blind to half the range.
            </span>
          </div>
          {readiness.weakest.length > 0 && readiness.weakest[0]!.images < readiness.minimumPerClass && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Photograph these next:</strong>{' '}
              {readiness.weakest
                .filter((w) => w.images < readiness.minimumPerClass)
                .slice(0, 6)
                .map((w) => `${w.name} (${w.images})`)
                .join(', ')}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="panel" style={{ color: 'var(--bad)', marginBottom: 12, whiteSpace: 'pre-wrap' }}>
          {error}
        </div>
      )}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 12 }}>{notice}</div>}

      <div className="panel">
        <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <input
            placeholder="Search code, name, family…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          {families.length > 0 && (
            <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              {families.length} famil{families.length === 1 ? 'y' : 'ies'}
            </span>
          )}
        </div>

        {shown.length === 0 ? (
          <div className="muted" style={{ padding: '18px 0' }}>
            No classes yet. Paste your list — the codes are what the MES knows these by.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr className="muted" style={{ fontSize: 12 }}>
                  <th style={cell} />
                  <th style={cell}>Code</th>
                  <th style={cell}>Name</th>
                  <th style={cell}>Family</th>
                  <th style={num}>Images</th>
                  <th style={num}>Model index</th>
                  <th style={cell}>State</th>
                  <th style={cell} />
                </tr>
              </thead>
              <tbody>
                {shown.map((k) => (
                  <tr key={k.id} style={{ opacity: k.active ? 1 : 0.5 }}>
                    <td style={{ ...cell, width: 56 }}>
                      <Reference klass={k} onChanged={load} />
                    </td>
                    <td style={{ ...cell, fontFamily: 'monospace', fontSize: 13 }}>
                      {k.code}
                      {k.rank !== null && (
                        <div className="muted" style={{ fontSize: 10 }}>
                          {k.scale ?? 'scale'} {k.rank}
                        </div>
                      )}
                    </td>
                    <td style={cell}>
                      <input
                        defaultValue={k.name}
                        onBlur={(e) =>
                          e.target.value !== k.name && void patch(k.id, { name: e.target.value })
                        }
                        style={{ width: 220, padding: '3px 6px', fontSize: 13 }}
                      />
                    </td>
                    <td style={cell}>
                      <input
                        defaultValue={k.family ?? ''}
                        placeholder="—"
                        onBlur={(e) =>
                          e.target.value !== (k.family ?? '') &&
                          void patch(k.id, { family: e.target.value })
                        }
                        style={{ width: 120, padding: '3px 6px', fontSize: 13 }}
                      />
                    </td>
                    <td
                      style={{
                        ...num,
                        color:
                          k.images === 0
                            ? 'var(--bad)'
                            : k.images < (readiness?.minimumPerClass ?? 300)
                              ? 'var(--warn, #f59e0b)'
                              : 'var(--good)',
                      }}
                    >
                      {k.images}
                    </td>
                    <td style={num} title="Position in the trained model's output vector. Never changes.">
                      {k.classIndex ?? '—'}
                    </td>
                    <td style={cell}>
                      <button
                        className="btn sm"
                        onClick={() => void patch(k.id, { active: !k.active })}
                        title={
                          k.active
                            ? 'Stop offering this class without losing the history that uses it'
                            : 'Offer this class again'
                        }
                      >
                        {k.active ? 'active' : 'inactive'}
                      </button>
                    </td>
                    <td style={cell}>
                      <button
                        className="btn sm"
                        style={{ color: 'var(--bad)' }}
                        onClick={() => void remove(k)}
                        title="Only possible while nothing uses it and no model knows it"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          Codes are fixed — they are what the MES knows these by. Names can be rephrased freely. A
          class a model has been trained on cannot be removed, only deactivated: its output index
          belongs to that model, and shifting it would relabel every prediction ever made.
        </p>
      </div>
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone?: 'good' | 'warn' | 'bad' }) {
  const colour =
    tone === 'good' ? 'var(--good)' : tone === 'bad' ? 'var(--bad)' : tone === 'warn' ? 'var(--warn, #f59e0b)' : undefined;
  return (
    <span>
      <strong style={{ fontSize: 20, color: colour }}>{n.toLocaleString('nl-NL')}</strong>{' '}
      <span className="muted">{label}</span>
    </span>
  );
}

/**
 * The reference image for a class, and how to change it.
 *
 * A thumbnail rather than a filename, because the point of a reference plate is
 * that you look at it. A grade is DEFINED by its plate — BMS 8 is not a
 * description, it is that picture — and an operator picking from eighty cuts
 * needs to see what "Ham deksel" is before a name means anything.
 *
 * Uploading replaces the primary rather than adding another. The common case is
 * "this is a better example than the one we had", and making that two actions —
 * upload, then promote — leaves the wrong one showing in between.
 */
function Reference({ klass, onChanged }: { klass: Klass; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('image', file);
      body.append('primary', 'true');
      body.append('caption', file.name);
      const res = await fetch(`/api/product-classes/${klass.id}/references`, {
        method: 'POST',
        body,
      });
      if (!res.ok) throw new Error((await res.json()).message ?? 'Upload failed');
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      title={
        error ??
        (klass.referenceKey
          ? 'Drop or click to replace the reference image'
          : 'Drop an image here, or click to choose one')
      }
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && !busy) void upload(file);
      }}
      style={{
        width: 44,
        height: 44,
        borderRadius: 5,
        cursor: busy ? 'wait' : 'pointer',
        border: error
          ? '1px solid var(--bad)'
          : over
            ? '2px solid var(--accent)'
            : '1px dashed var(--border)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: over ? 'var(--accent-dim)' : 'var(--bg-panel-2)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])}
      />
      {klass.referenceKey ? (
        <img
          src={`/api/images/${klass.referenceKey}`}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <span className="muted" style={{ fontSize: 16 }}>{busy ? '…' : '+'}</span>
      )}
    </div>
  );
}

/**
 * Dropping a whole grading chart, and cutting it into one plate per class.
 *
 * A grading standard arrives as one sheet — twelve plates in a grid with the
 * grade printed under each. Attached whole, every class shows the same picture
 * of all twelve, which is worse than no reference because it looks as though one
 * was set.
 *
 * So the sheet is cut here and the tiles are matched to classes IN THE ORDER
 * THEY ARE LISTED, left to right and top to bottom. That order is shown before
 * anything is uploaded, because a mislabelled reference plate is invisible
 * afterwards: the grader compares against the wrong picture and concludes the
 * meat is wrong.
 */
function ChartDrop({ classes, onChanged }: { classes: Klass[]; onChanged: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'detect' | 'grid'>('detect');
  const [cols, setCols] = useState('3');
  const [rows, setRows] = useState('4');
  const [tiles, setTiles] = useState<string[]>([]);
  /**
   * The printed names, cut out and set aside.
   *
   * Kept as pictures, not text. Reading them is where this would start guessing
   * — "SCHOUDER" against "SCHOUDERKARBONADE" is a coin flip between two real
   * cuts — but the hard part of a butcher's chart is not reading a word, it is
   * working out WHICH word belongs to which photograph when the leader lines
   * cross. Dragging solves that and leaves the reading to a person.
   */
  const [labels, setLabels] = useState<string[]>([]);
  /** What OCR made of each name. A suggestion shown beside the picture, never
      instead of it — a wrong read has to be visible in the same glance that
      confirms a right one. */
  const [readText, setReadText] = useState<ReadLabel[]>([]);
  const [reading, setReading] = useState<string | null>(null);
  /** Which name has been dropped on which tile. Index into `labels`. */
  const [named, setNamed] = useState<(number | null)[]>([]);
  /** Which class each tile goes to. Index-aligned with `tiles`; null = skip. */
  const [assign, setAssign] = useState<(number | null)[]>([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Grid mode: a grading standard, where the plates are laid out regularly and
   * their order IS the scale.
   */
  const cutGrid = useCallback(async (f: File, c: number, r: number) => {
    const bitmap = await createImageBitmap(f);
    const cw = bitmap.width / c;
    const ch = bitmap.height / r;
    const out: string[] = [];
    for (let y = 0; y < r; y += 1) {
      for (let x = 0; x < c; x += 1) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(cw);
        canvas.height = Math.floor(ch);
        canvas
          .getContext('2d')!
          .drawImage(bitmap, x * cw, y * ch, cw, ch, 0, 0, canvas.width, canvas.height);
        out.push(canvas.toDataURL('image/jpeg', 0.92));
      }
    }
    setTiles(out);
    setLabels([]);
    setNamed(out.map(() => null));
    // Grid order is meaningful, so classes line up positionally by default.
    setAssign(out.map((_, i) => classes[i]?.id ?? null));
    setNote(null);
  }, [classes]);

  /**
   * Detect mode: a butcher's chart, where cuts are scattered around a carcass
   * with leader lines. No grid lies over that, but every such chart is separate
   * photographs on one flat background — which is a threshold and a
   * connected-components pass, not a model.
   */
  const cutDetect = useCallback(async (f: File) => {
    setBusy(true);
    setError(null);
    try {
      const { plates } = await detectPlates(f);
      const photos = plates.filter((p) => p.kind === 'photo');
      const words = plates.filter((p) => p.kind === 'label');
      const crops = await Promise.all(photos.map((p) => cropPlate(f, p)));
      const wordCrops = await Promise.all(words.map((p) => cropPlate(f, p, 0.08)));
      setTiles(crops);
      setLabels(wordCrops);
      setReadText([]);
      setNamed(crops.map(() => null));
      // NOT auto-assigned. The chart prints the names beside the photographs and
      // reading them is exactly where this would go wrong: "SCHOUDER" against
      // "SCHOUDERKARBONADE" is a coin flip between two real cuts, and a misread
      // puts the wrong photograph on a class with nothing downstream to
      // contradict it.
      setAssign(crops.map(() => null));

      /**
       * Read the names, in the background, after the cuts are already on screen.
       *
       * Awaiting it would hold up the part that is immediately useful for a
       * multi-megabyte model download. The suggestions arrive when they arrive.
       */
      if (wordCrops.length > 0) {
        void readLabels(wordCrops, (done, total, msg) =>
          setReading(done < total ? `reading names ${done}/${total} (${msg})` : null),
        )
          .then((read) => {
            setReadText(read);
            setReading(null);
          })
          .catch(() => setReading(null));
      }
      setNote(
        `${crops.length} cut(s) and ${wordCrops.length} name(s) found. Drag a name onto the cut ` +
          'it belongs to, then pick its class. Leave the carcass diagram and any logo alone.',
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!file) return;
    if (mode === 'grid') void cutGrid(file, Number(cols) || 1, Number(rows) || 1);
    else void cutDetect(file);
  }, [file, mode, cols, rows, cutGrid, cutDetect]);

  const chosen = assign.filter((a) => a !== null).length;

  /**
   * The last line of defence for work that only exists in this tab.
   *
   * Assigning a class to a region changes nothing on the server; only Attach
   * does. Until then the browser holds the lot, and closing the tab throws it
   * away without a word. The sticky bar and the "not saved yet" wording are
   * there so it does not come to this — but if it does, the prompt costs a
   * keystroke and the alternative costs the whole chart.
   */
  useEffect(() => {
    if (chosen === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [chosen]);

  const attach = async () => {
    setBusy(true);
    setError(null);
    try {
      const duplicates = assign.filter((a): a is number => a !== null);
      if (new Set(duplicates).size !== duplicates.length) {
        // Two regions on one class would leave whichever uploaded last, silently.
        throw new Error('Two regions are assigned to the same class. Each class takes one plate.');
      }
      let done = 0;
      for (let i = 0; i < tiles.length; i += 1) {
        const classId = assign[i];
        if (classId === null || classId === undefined) continue;
        const blob = await (await fetch(tiles[i]!)).blob();
        const body = new FormData();
        body.append('image', blob, `plate-${i + 1}.jpg`);
        body.append('primary', 'true');
        body.append('caption', `${file?.name ?? 'chart'} region ${i + 1}`);
        const res = await fetch(`/api/product-classes/${classId}/references`, {
          method: 'POST',
          body,
        });
        if (!res.ok) throw new Error((await res.json()).message ?? `Failed on region ${i + 1}`);
        done += 1;
      }
      setNote(`${done} plate(s) attached.`);
      setFile(null);
      setTiles([]);
      setLabels([]);
      setNamed([]);
      setAssign([]);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        style={{
          border: over ? '2px solid var(--accent)' : '2px dashed var(--border)',
          background: over ? 'var(--accent-dim)' : 'transparent',
          borderRadius: 8,
          padding: '22px 18px',
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
        />
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {file ? file.name : 'Drop a chart or a poster here'}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          A butcher&apos;s chart with the cuts scattered around a carcass, or a grading standard
          laid out in a grid. For a single photograph, drop it on the thumbnail in the row instead.
        </div>
      </div>

      {file && (
        <>
          {/*
            Stuck to the top of the viewport, because this bar carries the only
            control that saves anything.

            It used to scroll away with the page. A chart of any size makes this
            panel two thousand pixels tall, so you drop the sheet, scroll down to
            put a class on each region — which is all of the work — and the
            "Attach" button is a thousand pixels above the fold with nothing on
            screen saying so. Every assignment lives in browser state until that
            click. The failure is silent and it looks exactly like success: the
            regions are found, the names are read, the dropdowns are filled, and
            not one byte has left the machine.

            That happened to real charts. Nothing reached the server and it took
            a look at the request log to establish it, because the page gives the
            same impression either way.
          */}
          <div
            className="row"
            style={{
              gap: 12,
              marginTop: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              position: 'sticky',
              top: 0,
              zIndex: 5,
              padding: '8px 10px',
              margin: '12px -10px 0',
              background: 'var(--bg-panel)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div className="row" style={{ gap: 6 }}>
              <button
                className={`btn sm${mode === 'detect' ? ' primary' : ''}`}
                onClick={() => setMode('detect')}
                title="Find the separate photographs on the sheet"
              >
                Find the cuts
              </button>
              <button
                className={`btn sm${mode === 'grid' ? ' primary' : ''}`}
                onClick={() => setMode('grid')}
                title="Cut a regular grid, for a grading standard"
              >
                Cut a grid
              </button>
            </div>
            {mode === 'grid' && (
              <>
                <label style={{ fontSize: 13 }}>
                  Columns{' '}
                  <input value={cols} onChange={(e) => setCols(e.target.value)} style={{ width: 48, padding: '3px 6px' }} />
                </label>
                <label style={{ fontSize: 13 }}>
                  Rows{' '}
                  <input value={rows} onChange={(e) => setRows(e.target.value)} style={{ width: 48, padding: '3px 6px' }} />
                </label>
              </>
            )}
            {/*
              "7 assigned" reads as "7 saved". It is not — it is seven things
              that will be gone if this tab closes. The wording says so, and says
              it in the warning colour, because the state it describes is a
              drawer full of work that no one has posted.
            */}
            <span
              style={{
                fontSize: 13,
                color: chosen > 0 && !busy ? 'var(--warn, #f59e0b)' : 'var(--text-dim)',
              }}
            >
              {busy
                ? 'Working…'
                : chosen > 0
                  ? `${chosen} of ${tiles.length} region(s) assigned — not saved yet`
                  : `${tiles.length} region(s), none assigned yet`}
            </span>
            <div style={{ flex: 1 }} />
            <button className="btn sm" onClick={() => { setFile(null); setTiles([]); setLabels([]); setNamed([]); setAssign([]); setError(null); setNote(null); }}>
              Cancel
            </button>
            <button className="btn primary sm" disabled={busy || chosen === 0} onClick={() => void attach()}>
              {busy ? 'Attaching…' : `Attach ${chosen} plate(s)`}
            </button>
          </div>

          {note && <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>{note}</div>}

          {labels.length > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 6,
                background: 'var(--bg-panel-2)',
                border: '1px solid var(--border)',
              }}
            >
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Names found on the sheet — drag one onto the cut it belongs to. Ones already placed
                are dimmed. {reading && <em>{reading}</em>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {labels.map((src, i) => {
                  const used = named.includes(i);
                  const read = readText[i];
                  return (
                    <div
                      key={i}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(i))}
                      title={used ? 'Already placed — drag it again to move it' : 'Drag onto a cut'}
                      style={{
                        cursor: 'grab',
                        opacity: used ? 0.35 : 1,
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: 3,
                        background: 'var(--bg-panel)',
                      }}
                    >
                      {/* The picture stays. The text is a suggestion, and a
                          suggestion you cannot check is just an assertion. */}
                      <img
                        src={src}
                        alt=""
                        style={{ height: 20, display: 'block', background: '#fff', borderRadius: 2 }}
                      />
                      {read && (
                        <div
                          style={{
                            fontSize: 10,
                            marginTop: 2,
                            maxWidth: 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color:
                              read.confidence >= 70
                                ? 'var(--text-dim)'
                                : 'var(--warn, #f59e0b)',
                          }}
                          title={`${read.text || '(nothing read)'} — OCR confidence ${read.confidence}%`}
                        >
                          {read.text || '—'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
              gap: 10,
              marginTop: 12,
            }}
          >
            {tiles.map((src, i) => (
              <div key={i}>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const which = Number(e.dataTransfer.getData('text/plain'));
                    if (Number.isNaN(which)) return;
                    setNamed((n) => {
                      const next = [...n];
                      // A name belongs to one cut. Dropping it again moves it
                      // rather than duplicating, so the tray always shows what
                      // is still unplaced.
                      for (let k = 0; k < next.length; k += 1) if (next[k] === which) next[k] = null;
                      next[i] = which;
                      return next;
                    });
                    /**
                     * Pre-select the class the read name most resembles.
                     *
                     * Only on a near match — a poor guess a tired operator
                     * accepts is worse than none, because they would have had to
                     * look either way and only one of those states makes them.
                     */
                    const guess = suggestClass(readText[which]?.text ?? '', classes);
                    if (guess) {
                      setAssign((a) => {
                        const next = [...a];
                        if (!next[i]) next[i] = guess.klass.id;
                        return next;
                      });
                    }
                  }}
                  style={{
                    aspectRatio: '1',
                    background: 'var(--bg-panel-2)',
                    borderRadius: 5,
                    overflow: 'hidden',
                    position: 'relative',
                    border: assign[i] ? '2px solid var(--good)' : '1px solid var(--border)',
                  }}
                >
                  <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  {named[i] !== null && named[i] !== undefined && (
                    <img
                      src={labels[named[i]!]}
                      alt=""
                      onClick={(e) => {
                        e.stopPropagation();
                        setNamed((n) => {
                          const next = [...n];
                          next[i] = null;
                          return next;
                        });
                      }}
                      title="Click to take this name off again"
                      style={{
                        position: 'absolute',
                        left: 4,
                        bottom: 4,
                        right: 4,
                        height: 20,
                        objectFit: 'contain',
                        background: '#fff',
                        borderRadius: 3,
                        padding: '1px 3px',
                        cursor: 'pointer',
                      }}
                    />
                  )}
                </div>
                <select
                  value={assign[i] ?? ''}
                  onChange={(e) =>
                    setAssign((a) => {
                      const next = [...a];
                      next[i] = e.target.value ? Number(e.target.value) : null;
                      return next;
                    })
                  }
                  style={{ width: '100%', marginTop: 4, fontSize: 12, padding: '2px 4px' }}
                >
                  <option value="">— skip —</option>
                  {classes.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.code} {k.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 10 }}>{error}</div>}
    </div>
  );
}
